import { z } from 'zod'

import { istGast, type PersonRole } from './roles'

/**
 * **Workspaces – Arbeitswelten** ([Z-07](../../../docs/phasen/zusatz-07-workspaces.md)).
 *
 * Nils, 25.08.2026: *„Ich möchte Work gerne auch für andere Zwecke nutzen. … alles was jetzt
 * ist – Workspace «Entwicklung». … Und ich könnte geteilte Workspaces machen wie bspw.
 * Marketing – oder Technischer Service usw."*
 *
 * ## Das dritte Wort – und warum es mit den zwei anderen nicht kollidiert
 *
 * `CLAUDE.md` warnt vor der Verwechslung von **Bereich** (`scope`, ein Repository) und
 * **Vorhaben** (ein Stück geplanter Arbeit). Ein **Workspace** ist eine dritte Ebene, und die
 * Beziehung ist eine Schachtelung statt einer Kreuzung: ein Workspace enthält Vorhaben, ein
 * Vorhaben berührt Bereiche. Ein Bereich gehört zu keinem Workspace – GitLab ist GitLab.
 *
 * **Das englische Wort bleibt mit Absicht.** „Arbeitsbereich" wäre die Übersetzung und wäre
 * genau der Fehler: „Bereich" ist vergeben, und „Mein Bereich" ist bereits ein Menüpunkt für
 * etwas ganz anderes (die Fachbereichs-Ansicht). Ein Fremdwort, das mit nichts kollidiert, ist
 * hier klarer als ein deutsches, das mit zweien kollidiert.
 */

/**
 * **Der Workspace, in den bei der Migration alles wandert.**
 *
 * Steht als Konstante da, weil Migration, Startbelegung und Tests denselben meinen müssen.
 * Er ist danach ein ganz gewöhnlicher geteilter Workspace – kein Sonderfall im Code, nur der
 * erste.
 */
export const STANDARD_WORKSPACE = 'entwicklung'

/**
 * **Die Antwort auf eine ungültige Kopfzeile `x-workspace`** – geteilt, weil der Client daran
 * erkennt, dass er einen gemerkten Workspace vergessen muss (17.09.2026). Zwei gleich lautende
 * Zeichenketten wären eine Verabredung, die beim Umformulieren lautlos zerbricht.
 */
export const WORKSPACE_UNBEKANNT = 'Diesen Workspace gibt es hier nicht.'

export const WORKSPACE_ARTEN = ['geteilt', 'persoenlich'] as const
export type WorkspaceArt = (typeof WORKSPACE_ARTEN)[number]
export const workspaceArtSchema = z.enum(WORKSPACE_ARTEN)

export const WORKSPACE_ART_LABELS: Record<WorkspaceArt, string> = {
  geteilt: 'Geteilt',
  persoenlich: 'Persönlich',
}

/**
 * Kennungen sind sprechender Text, kein ULID – dieselbe Begründung wie bei `pool` und
 * `source`: ein Workspace kommt in Konfiguration, Protokollen und Gesprächen vor, und
 * `marketing` ist dort lesbar, `01KYQ7EF…` nicht.
 *
 * Das Muster ist eng gehalten (Kleinbuchstaben, Ziffern, Bindestrich), weil die Kennung in
 * zusammengesetzten Pool-Kennungen und in URLs auftaucht.
 */
export const WORKSPACE_ID_MUSTER = /^[a-z0-9][a-z0-9-]{1,31}$/
export const workspaceIdSchema = z
  .string()
  .regex(WORKSPACE_ID_MUSTER, 'Nur Kleinbuchstaben, Ziffern und Bindestriche, 2 bis 32 Zeichen.')

/**
 * **Das Kürzel einer Arbeitswelt – drei bis acht Zeichen, für den Betreff einer Mail**
 * (28.08.2026, [E-272](../../../docs/ENTSCHEIDUNGEN.md#e-272)).
 *
 * Nils: *„Kriegen wir noch Workspace Kürzel hin … dann könnte ich bspw. «#NW8» schreiben"* – und
 * kurz darauf, nachdem die erste Fassung stand: *„Und Kürzel dürfen 3-8 Stellen haben."*
 *
 * ## Warum die Untergrenze bei **drei** liegt
 *
 * Ein oder zwei Zeichen hinter einem `#` stehen in echten Betreffzeilen ständig – „#1",
 * „#3 Nachtrag", „#AB offen", jede Ticketnummer. Ein Kürzel, das versehentlich greift, schickt
 * eine Meldung in eine Arbeitswelt, in der der Rest des Teams sie nicht sieht; das ist genau der
 * Fehler, den niemand meldet, weil niemand eine Zeile vermisst, die er nie gesehen hat.
 *
 * ## Und warum acht oben reichen
 *
 * Weil es von Hand getippt wird, oft am Telefon durchgegeben. Wer `#TECHNISCHER-SERVICE` schreiben
 * müsste, schreibt es einmal falsch und danach gar nicht mehr. Acht Zeichen tragen ein sprechendes
 * Kürzel (`MARKETNG`) und bleiben eine Sache von zwei Sekunden.
 *
 * ## Warum Großbuchstaben und Ziffern, sonst nichts
 *
 * Bindestriche, Punkte und Umlaute machen aus dem Kürzel eine Frage („war das `NW-8` oder
 * `NW.8`?"). Gespeichert und verglichen wird in Großschreibung – `#nw8` und `#NW8` sind dasselbe.
 *
 * ## Und warum mindestens **ein Buchstabe** darin stehen muss
 *
 * Weil eine reine Ziffernfolge hinter einem `#` genau das ist, was in Betreffzeilen ohnehin
 * steht: „Rechnung #4711", „Auftrag #90210". Solange die Obergrenze bei drei Zeichen lag, war das
 * kein Thema; mit acht trifft das Muster jede gängige Belegnummer. Ein Buchstabe kostet niemanden
 * etwas – `NW8`, `MARKETNG`, `TS2` haben ohnehin einen – und macht den Zufallstreffer unmöglich.
 */
export const WORKSPACE_KUERZEL_MUSTER = /^(?=[A-Z0-9]{3,8}$)[A-Z0-9]*[A-Z][A-Z0-9]*$/
export const workspaceKuerzelSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(
    WORKSPACE_KUERZEL_MUSTER,
    'Drei bis acht Zeichen, Großbuchstaben und Ziffern – mindestens ein Buchstabe.',
  )

/**
 * **Aus einer Beschriftung eine Kennung machen** – „Technischer Service" → `technischer-service`.
 *
 * Umlaute werden ausgeschrieben und nicht weggeworfen: `Büro` soll `buero` werden und nicht
 * `bro`. Die Zerlegung nach `NFD` allein täte das Falsche – sie macht aus `ü` ein `u`, und
 * damit wären „Büro" und „Buro" dieselbe Kennung.
 */
export function workspaceIdAus(label: string): string {
  const ersetzt = label
    .toLowerCase()
    .replaceAll('ä', 'ae')
    .replaceAll('ö', 'oe')
    .replaceAll('ü', 'ue')
    .replaceAll('ß', 'ss')
  return ersetzt
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
}

/**
 * **Wer sich einen persönlichen Workspace anlegen darf** (Nils, 25.08.2026: *„Eben jeder kann
 * sich den anlegen außer nur die Gäste."*).
 *
 * Als Negativregel geschrieben – „alle außer Gast" –, weil Nils sie so gesagt hat und weil sie
 * so bleibt, wenn eine sechste Rolle dazukommt. Eine Positivliste müsste dann erweitert werden,
 * und wer sie vergisst, sperrt eine neue Rolle aus, ohne es zu merken.
 *
 * **Der Mit-Owner ist ausdrücklich dabei.** Seine eingeschränkte Sicht auf die *geteilten*
 * Workspaces bleibt davon unberührt: die Workspace-Grenze kommt in `aufgabenSichtbarkeit()` als
 * **zusätzliche** Bedingung dazu, nie als Ersatz für die Vorhaben-Prüfung.
 */
export function darfWorkspaceAnlegen(role: PersonRole): boolean {
  return !istGast(role)
}

/**
 * **Welches WIP-Limit gilt – und wogegen es zählt** ([Z-07](../../../docs/phasen/zusatz-07-workspaces.md),
 * Entscheidung 8).
 *
 * Nils, 25.08.2026: *„Ja – außer man hat ein WIP pro Workspace (Sonst greift das globale)"*,
 * und auf die Rückfrage: *„ja genau – Ersatz … wer überall ein eigenes Limit hat, hat über
 * alles keine Obergrenze – 3 + 3 + 3 wären erlaubt."*
 *
 * | `workspace_mitglied.wip_limit` | Limit | gezählt über |
 * |---|---|---|
 * | eine Zahl | diese Zahl | nur die Aufgaben **dieses** Workspace |
 * | `null` | `person.wip_limit` | **alle** Workspaces der Person |
 *
 * ## Warum Ersatz und nicht „beide müssen halten"
 *
 * Weil ein Workspace-Limit jemand **absichtlich** setzt, und wer es setzt, hat für diesen
 * Workspace eine eigene Vorstellung von „voll". Ein globales Limit, das darüber hinweg trotzdem
 * greift, kassiert diese Vorstellung stillschweigend – und der Betroffene bekommt eine Absage,
 * deren Zahl in **keiner** Einstellung steht. Eine Grenze, die man nicht nachlesen kann, ist
 * keine Grenze, sondern ein Fehler.
 *
 * Die getragene Folge steht oben in Nils' Zitat und ist bewusst hingenommen.
 *
 * ## Warum das hier steht und nicht im Server
 *
 * Damit Oberfläche und Server dieselbe Rechnung benutzen – derselbe Grund wie bei `darfStarten`
 * direkt daneben. Zeigt „Mein Tag" ein anderes Limit an, als der Server durchlässt, ist die
 * Absage für den Benutzer nicht nachvollziehbar.
 */
export interface WipHerkunft {
  /**
   * Das Limit, das gilt – **`null` heißt „keine Grenze"**.
   *
   * Tritt genau in einem Fall ein: eine Aufgabe im **persönlichen** Workspace, für den kein
   * eigenes Limit hinterlegt ist. Siehe unten.
   */
  limit: number | null
  /** Welche Aufgaben dagegen zählen. */
  zaehlt: 'workspace' | 'geteilte'
}

export function wipHerkunft(lage: {
  personWipLimit: number
  workspaceWipLimit: number | null | undefined
  /** Die Art des Workspace, in dem die Aufgabe liegt. */
  workspaceArt: WorkspaceArt
}): WipHerkunft {
  const eigenes = lage.workspaceWipLimit
  if (eigenes !== null && eigenes !== undefined) {
    return { limit: eigenes, zaehlt: 'workspace' }
  }
  /**
   * **Im persönlichen Workspace gilt ohne eigenes Limit gar keins** (Nils, 25.08.2026).
   *
   * Das globale hier greifen zu lassen wäre die naheliegende Variante und wäre falsch in
   * **beide** Richtungen: eigene Todos zählten in die Abteilungsrechnung hinein *und* die
   * Abteilungsarbeit sperrte das Abhaken eigener Todos. „Überweisung machen" ist keine
   * angefangene Aufgabe im Sinne von WIP.
   *
   * Wer für sich selbst eine Grenze will, setzt sie am Workspace – dann greift der Zweig
   * darüber.
   */
  if (lage.workspaceArt === 'persoenlich') return { limit: null, zaehlt: 'workspace' }

  return { limit: lage.personWipLimit, zaehlt: 'geteilte' }
}

/**
 * **In der eigenen persönlichen Arbeitswelt gehört alles dir – ohne Zuweisung**
 * (28.08.2026, [E-278](../../../docs/ENTSCHEIDUNGEN.md#e-278)).
 *
 * Nils: *„Mein Tag ist doch eigentlich workspace losgelöst … ich finde da zumindest nicht meine
 * privaten."*
 *
 * Gemessen an seinem Bestand war das der größere von zwei Gründen: **alle elf** Aufgaben in
 * „Meine ToDos" hatten weder `assignee_id` noch `work_assignee_id`. „Mein Tag" fragt aber nach
 * dem Bearbeiter – also fielen sie aus jeder Zone heraus, auch aus den unverengten.
 *
 * Und das ist kein Versäumnis, das man durch elf Klicks heilt: **in einer persönlichen
 * Arbeitswelt sich selbst eine Aufgabe zuzuweisen ist eine Zeremonie ohne Gegenüber.** Es gibt
 * dort niemand anderen ([E-233](../../../docs/ENTSCHEIDUNGEN.md#e-233): sie hat genau ein
 * Mitglied). Die Zuweisung trägt keine Information, also darf sie auch nicht Bedingung sein.
 *
 * **Warum `gehoertMir` und nicht `art === 'persoenlich'` allein:** die Art sagt, dass es eine
 * persönliche Welt ist, nicht *wessen*. Heute hat jede genau ein Mitglied, und damit wäre die
 * kürzere Prüfung zufällig richtig – „zufällig richtig" ist die Bauform, die still falsch wird,
 * sobald jemand einen zweiten Menschen hineinlässt.
 */
export function gehoertMirImplizit(
  welt: { art: WorkspaceArt; gehoertMir: boolean } | null | undefined,
): boolean {
  return !!welt && welt.art === 'persoenlich' && welt.gehoertMir
}

/**
 * **Der Satz, mit dem eine Absage ehrlich bleibt, ohne zu verraten wo.**
 *
 * Aus Entscheidung 3 folgt, dass der Leitstand **nie** über Workspaces hinweg zusammenfasst.
 * Damit kann er nicht erklären, warum jemand mit „1 von 4" trotzdem nichts mehr anfangen darf –
 * die anderen drei liegen woanders. Der einzige Ort, an dem das sichtbar werden darf, ist die
 * Absage selbst, und sie nennt **keinen** fremden Workspace: der könnte ein persönlicher sein.
 */
export function wipHinweis(herkunft: WipHerkunft): string {
  return herkunft.zaehlt === 'geteilte'
    ? 'Aufgaben aus deinen anderen geteilten Workspaces zählen mit – persönliche nicht.'
    : 'Für diesen Workspace gilt ein eigenes Limit.'
}

/**
 * **Woran der Zähler gemessen wird – in drei Wörtern** (Nils, 02.10.2026, bei der Abnahme von
 * Z-19).
 *
 * Über drei sichtbar laufenden Karten stand *„0 von 7 Plätzen"*. Nach der Regel stimmte das:
 * Die drei lagen in persönlichen Welten, und die zählen nicht mit ([E-238](../../../docs/ENTSCHEIDUNGEN.md#e-238)).
 * Gelesen hat es sich wie ein Widerspruch – eine Zahl, die man nicht mit dem zusammenbringt, was
 * darunter steht, ist schlimmer als keine.
 *
 * {@link wipHinweis} sagt dasselbe ausführlicher, steht aber nur da, **wenn der Fokus voll ist**.
 * Bei 0 von 7 erschien es nie, und genau dort entsteht die Verwirrung.
 *
 * ## Warum nicht einfach der Name der aktiven Welt
 *
 * Weil das in dem Fall, der hier auffiel, **falsch** wäre. Ohne eigenes Limit am Workspace gilt
 * das Limit der Person, und das zählt über **alle** geteilten Welten (`zaehlt: 'geteilte'`).
 * „0 von 7 in Entwicklung" wäre eine Zahl, die etwas anderes misst, als sie behauptet – also
 * derselbe Fehler noch einmal, nur in die andere Richtung.
 */
export function wipBereichKurz(herkunft: WipHerkunft, weltName?: string | null): string {
  if (herkunft.zaehlt === 'geteilte') return 'in deinen geteilten Welten'
  return weltName ? `in ${weltName}` : 'in dieser Arbeitswelt'
}

/* ── Farben (26.08.2026) ─────────────────────────────────────────────── */

/**
 * **Jede Arbeitswelt bekommt eine Farbe.**
 *
 * Nils, 26.08.2026: *„Bei den Seiten wo die Trennung greift, da im Header zentral auch das
 * aktive Workspace immer anzeigen (Leitstand, Pools, Alle Aufgaben etc.), dass man das
 * irgendwie gut sieht – ggf. auch die Farbe des Workspaces nehmen (Workspaces müssen dann
 * Farben kriegen können oder so)."*
 *
 * ## Warum eine feste Auswahl und kein Farbwähler
 *
 * Weil die Farben dieses Systems **gemessen** sind: die Pool-Farben wurden in AP 2.3 gegen
 * Farbfehlsichtigkeit geprüft, und der Parkplatz bekam ausdrücklich Grau statt einer vierten
 * erfundenen Farbe. Ein freier Wähler macht aus diesem Nachweis eine Empfehlung – die erste
 * Arbeitswelt in Hellgelb auf Weiß entwertet ihn, ohne dass es jemandem auffällt.
 *
 * Sechs Töne reichen für sechs Arbeitswelten. Braucht es eine siebte, ist das eine Entscheidung
 * mit einer Messung und keine Eingabe in einem Formular.
 *
 * ## Warum hier nur die Namen stehen
 *
 * Welches Blau `blau` ist, beantwortet `theme.css`. Ein Hex-Wert im geteilten Paket wäre eine
 * zweite Stelle, an der das Erscheinungsbild festgelegt ist – und die, die man beim nächsten
 * Anstrich vergisst.
 */
export const WORKSPACE_FARBEN = ['grau', 'blau', 'gruen', 'orange', 'violett', 'rot'] as const
export type WorkspaceFarbe = (typeof WORKSPACE_FARBEN)[number]
export const workspaceFarbeSchema = z.enum(WORKSPACE_FARBEN)

export const WORKSPACE_FARB_LABELS: Record<WorkspaceFarbe, string> = {
  grau: 'Grau',
  blau: 'Blau',
  gruen: 'Grün',
  orange: 'Orange',
  violett: 'Violett',
  rot: 'Rot',
}

/**
 * **Die Vorgabe ist Grau, nicht Blau.**
 *
 * Blau ist die Akzentfarbe der Anwendung – Knöpfe, aktive Menüpunkte, Marken. Eine Arbeitswelt,
 * die ungefragt dieselbe Farbe trägt, sieht aus wie ein Bedienelement. Grau sagt „hat noch keine
 * Farbe" und drängt sich nicht vor; wer eine will, wählt sie.
 */
export const WORKSPACE_FARBE_VORGABE: WorkspaceFarbe = 'grau'
